import type { KarlSourceEntry } from "../../KarlSourcesPhase/sourceRoster";
import type { GapTranslate } from "../../findCableGaps";
import type { KarlAnswer, KarlAsk, KarlAskTool } from "../types";
import type { BuildBoardAsks, BuildBoardAsksArgs } from "./types";
import type { MainBoardView } from "../../mainBoard";
import type { KarlBoardFeeds, KarlFeeders, KarlHeights } from "../../CablesChatPanel/types";
import { heightAnswers } from "../buildHeightAsks";
import { CATEGORY_META, CATEGORY_ORDER } from "../../deviceCategories";
import type { DeviceCategory } from "../../deviceCategories";

export const BOARDS_COMPLETE_ASK_ID = "source:complete:boards";
export const MAIN_BOARD_ASK_ID = "source:main:floor";

type BoardAskArgs = Omit<
  BuildBoardAsksArgs,
  "roster" | "answeredKeys" | "names" | "feeds" | "heights" | "feeders" | "confirmedKeys"
> & {
  entry: KarlSourceEntry;
  /** What he calls the board now: his rename, else the run's name. */
  name: string;
  /** The main board's marker label, for a board struck with circuits still on it. */
  mainLabel: string | null;
};

const line = (
  t: GapTranslate,
  key: string,
  params?: Record<string, string | number>,
): string => t(`smartSurveyor.cablesBeta.${key}`, params);

const later = (t: GapTranslate): KarlAnswer => ({
  id: "later",
  label: line(t, "askLater"),
  tone: "quiet",
  action: { kind: "skip" },
});

const boardTools = ({ entry, mainLabel }: BoardAskArgs): KarlAskTool[] => {
  const rename: KarlAskTool[] = entry.label
    ? [
        {
          id: "rename",
          labelKey: "askToolRenameBoard",
          action: { kind: "renameSource", label: entry.label },
        },
      ]
    : [];
  // Striking the main board would leave every run on the floor fed from nowhere.
  if (entry.isMain) return rename;
  const strikeKey = entry.label || entry.key;
  const handsOn =
    entry.label &&
    entry.circuitCount > 0 &&
    mainLabel &&
    mainLabel !== entry.label;
  return [
    ...rename,
    {
      id: "notABoard",
      labelKey: "askToolNotABoard",
      action: handsOn
        ? { kind: "removeSourceTo", label: entry.label, toLabel: mainLabel }
        : { kind: "removeSource", label: strikeKey },
    },
  ];
};

const focusOf = ({ entry, markers }: BoardAskArgs) => {
  const position = entry.position ?? markers[entry.board];
  return position ? { focus: { position } } : {};
};

const placementAsk = (args: BoardAskArgs): KarlAsk => {
  const { entry, name, t } = args;
  const key = entry.isMain
    ? entry.decidedBy === "note"
      ? "askMainNote"
      : "askMain"
    : "askPlacement";
  return {
    id: `source:place:${entry.key}`,
    group: "sources",
    question: line(t, key, { board: name }),
    detail: entry.isMain ? line(t, "askMainWholeFloor") : undefined,
    subject: name,
    subjectKey: entry.key,
    ...focusOf(args),
    tools: boardTools(args),
    answers: [
      {
        id: "right",
        label: line(t, "askYesRight"),
        tone: "primary",
        action: { kind: "accept" },
      },
      ...(entry.label
        ? [
            {
              id: "move",
              label: line(t, "askMoveIt"),
              action: { kind: "moveSource" as const, label: entry.label },
            },
          ]
        : []),
      later(t),
    ],
  };
};

const guessedAsk = (args: BoardAskArgs): KarlAsk => {
  const { entry, name, t } = args;
  return {
    id: `source:guess:${entry.key}`,
    group: "sources",
    question: line(t, "askGuessed", {
      board: name,
      devices: entry.deviceCount,
    }),
    subject: name,
    subjectKey: entry.key,
    ...focusOf(args),
    tools: boardTools(args),
    answers: [
      {
        id: "show",
        label: line(t, "askShowWhere"),
        tone: "primary",
        action: entry.label
          ? { kind: "moveSource", label: entry.label }
          : { kind: "placeSource", name: entry.board },
      },
      {
        id: "close",
        label: line(t, "askCloseEnough"),
        action: { kind: "accept" },
      },
      later(t),
    ],
  };
};

const absentAsk = (args: BoardAskArgs): KarlAsk => {
  const { entry, name, scheduleBoard, t } = args;
  return {
    id: `source:absent:${entry.key}`,
    group: "sources",
    tools: boardTools(args),
    question: line(
      t,
      entry.board === scheduleBoard ? "askAbsentSchedule" : "askAbsent",
      { board: name },
    ),
    subject: name,
    subjectKey: entry.key,
    answers: [
      {
        id: "here",
        label: line(t, "askItsHere"),
        tone: "primary",
        action: { kind: "placeSource", name: entry.board },
      },
      {
        id: "elsewhere",
        label: line(t, "askNotThisFloor"),
        action: { kind: "accept" },
      },
      later(t),
    ],
  };
};

// "Geht von dort Kabel ab?" was asked here: meaningless for a board, and its "no" changed nothing.
// The consider ask settles whether the board counts; what feeds it is asked next.
const checkAsk = (args: BoardAskArgs): KarlAsk | null => {
  const { entry, markers } = args;
  if (entry.isStruck) return null;
  if (entry.status === "open") {
    return entry.position ?? markers[entry.board] ? placementAsk(args) : absentAsk(args);
  }
  if (entry.status === "absent") return absentAsk(args);
  if (entry.status === "estimated") return guessedAsk(args);
  if (entry.position) return placementAsk(args);
  return null;
};

// First, whether the board counts at all. "Nein" strikes it, and everything else about it goes with it.
const considerAsk = (args: BoardAskArgs): KarlAsk | null => {
  const { entry, name, mainLabel, t } = args;
  if (entry.isMain || !entry.label || entry.status === "absent") return null;
  const handsOn = entry.circuitCount > 0 && mainLabel && mainLabel !== entry.label;
  return {
    id: `source:consider:${entry.key}`,
    group: "sources",
    question: line(t, "askConsider", { board: name }),
    subject: name,
    subjectKey: `consider:${entry.key}`,
    ...focusOf(args),
    answers: [
      { id: "yes", label: line(t, "askConsiderYes"), tone: "primary", action: { kind: "accept" } },
      {
        id: "no",
        label: line(t, "askConsiderNo"),
        action: handsOn
          ? { kind: "removeSourceTo", label: entry.label, toLabel: mainLabel }
          : { kind: "removeSource", label: entry.label },
      },
      later(t),
    ],
  };
};

// Then what feeds it — only once he said it counts. "Another floor" is a tool: a fourth rung is a menu.
const fedFromAsk = (
  args: BoardAskArgs,
  feeders: KarlFeeders,
  confirmedKeys: Set<string>,
): KarlAsk | null => {
  const { entry, name, t } = args;
  const view = entry.label ? feeders.byLabel[entry.label] : undefined;
  if (!view || entry.isStruck || entry.isMain) return null;
  const isAnswered = view.isStored || view.isOffFloor;
  if (!isAnswered && !confirmedKeys.has(`consider:${entry.key}`)) return null;
  return {
    id: `source:fedFrom:${entry.key}`,
    group: "sources",
    question: line(t, "askFedFromBoard", { board: name, from: view.fromName }),
    detail: line(t, "askFedFromDetail"),
    subject: name,
    subjectKey: `fedFrom:${entry.key}`,
    ...focusOf(args),
    tools: [
      {
        id: "offFloor",
        labelKey: "askToolOffFloor",
        action: { kind: "setFeederOffFloor", toLabel: entry.label },
      },
    ],
    answers: [
      {
        id: "yes",
        label: line(t, "askFedFromYes", { from: view.fromName }),
        tone: "primary",
        action: { kind: "setFeederFrom", toLabel: entry.label, fromLabel: view.fromLabel },
      },
      { id: "other", label: line(t, "askFedFromOther"), action: { kind: "pickUpstreamFor", toLabel: entry.label } },
      later(t),
    ],
  };
};

// Last, the feeder's cable: sized by load, which no plan shows, so Karl suggests and he confirms.
const feederTypeAsk = (args: BoardAskArgs, feeders: KarlFeeders): KarlAsk | null => {
  const { entry, name, t } = args;
  const view = entry.label ? feeders.byLabel[entry.label] : undefined;
  if (!view || entry.isStruck || !view.isStored || view.isOffFloor) return null;
  const cableType = view.cableType ?? feeders.suggestion;
  return {
    id: `source:feederType:${entry.key}`,
    group: "sources",
    question: line(t, "askFeederType", { from: view.fromName, board: name, type: cableType }),
    detail: line(t, "askFeederTypeDetail"),
    subject: name,
    subjectKey: `feederType:${entry.key}`,
    ...focusOf(args),
    answers: [
      {
        id: "yes",
        label: line(t, "askFeederTypeYes", { type: cableType }),
        tone: "primary",
        action: { kind: "setFeederType", toLabel: entry.label, cableType },
      },
      {
        id: "other",
        label: line(t, "askFeederTypeOther"),
        action: { kind: "editFeederType", toLabel: entry.label, cableType, options: feeders.cableTypes },
      },
      later(t),
    ],
  };
};

const categoryList = (t: GapTranslate, categories: DeviceCategory[]): string =>
  categories.map((category) => t(CATEGORY_META[category].i18nKey)).join(", ");

const deviceCount = (counts: Record<string, number> | undefined): number =>
  Object.values(counts ?? {}).reduce((sum, count) => sum + count, 0);

// Asked for every live board: which categories it feeds decides which devices it may take.
const feedsAsk = (args: BoardAskArgs, feeds: KarlBoardFeeds): KarlAsk | null => {
  const { entry, name, t } = args;
  const label = entry.label || entry.key;
  const resolved = feeds.byLabel[label];
  if (!resolved || entry.isStruck || entry.status === "absent") return null;
  const categories = resolved.feed.categories;
  const onBoard = feeds.countsByBoard[label] ?? {};
  const devices = CATEGORY_ORDER.reduce((sum, category) => sum + deviceCount(onBoard[category]), 0);
  return {
    id: `source:feeds:${entry.key}`,
    group: "sources",
    question: categories.length
      ? line(t, "askFeeds", { board: name, categories: categoryList(t, categories) })
      : line(t, "askFeedsNothing", { board: name }),
    detail: line(t, "askFeedsDetail", { devices }),
    subject: name,
    subjectKey: `feeds:${entry.key}`,
    ...focusOf(args),
    tools: [
      { id: "editFeeds", labelKey: "askToolEditFeeds", action: { kind: "editBoardFeeds", label } },
    ],
    answers: [
      {
        id: "right",
        label: line(t, "askFeedsRight"),
        tone: "primary",
        action: { kind: "setBoardFeeds", label, feed: resolved.feed },
      },
      later(t),
    ],
  };
};

// Where the cable leaves the board decides how far it climbs to the tray.
const heightAsk = (args: BoardAskArgs, heights: KarlHeights): KarlAsk | null => {
  const { entry, name, t } = args;
  if (!entry.label || entry.isStruck || entry.status === "absent") return null;
  const target = { kind: "board" as const, label: entry.label };
  const heightM = heights.resolved.boardM(entry.label);
  return {
    id: `source:height:${entry.key}`,
    group: "sources",
    question: line(t, "askBoardHeight", { board: name, height: heights.format(heightM) }),
    detail: line(t, "askBoardHeightDetail"),
    subject: name,
    subjectKey: `height:${entry.key}`,
    ...focusOf(args),
    answers: heightAnswers(t, target, heightM),
  };
};

const unfedAsk = (t: GapTranslate, category: DeviceCategory, devices: number): KarlAsk => ({
  id: `source:unfed:${category}`,
  group: "sources",
  question: line(t, "askUnfed", { devices, category: t(CATEGORY_META[category].i18nKey) }),
  subjectKey: `unfed:${category}`,
  answers: [
    {
      id: "show",
      label: line(t, "askUnfedShow"),
      tone: "primary",
      action: { kind: "pickFeederFor", category },
    },
    {
      id: "unbilled",
      label: line(t, "askUnfedUnbilled"),
      action: { kind: "leaveCategoryUnbilled", category },
    },
    // Settles the question without changing anything: he adds that board himself when he gets to it.
    {
      id: "myselfLater",
      label: line(t, "askUnfedLater"),
      tone: "quiet",
      action: { kind: "accept" },
    },
  ],
});

const zoneAsk = (args: BoardAskArgs): KarlAsk | null => {
  const { entry, name, zoneCountByLabel, t } = args;
  // The main board's zone is the floor itself; a board with no marker has nowhere to draw from.
  if (entry.isMain || !entry.label || !entry.position || entry.isStruck)
    return null;
  const zones = zoneCountByLabel[entry.label] ?? 0;
  const draw = {
    kind: "drawZone" as const,
    label: entry.label,
  };
  return {
    id: `source:zone:${entry.key}`,
    group: "sources",
    question: zones
      ? line(t, "askZoneHas", { board: name, count: zones })
      : line(t, "askZone", { board: name }),
    detail: zones ? undefined : line(t, "askZoneDetail", { board: name }),
    subject: name,
    subjectKey: `zone:${entry.key}`,
    ...focusOf(args),
    answers: zones
      ? [
          {
            id: "right",
            label: line(t, "askZoneThatsAll"),
            tone: "primary",
            action: { kind: "accept" },
          },
          { id: "draw", label: line(t, "askZoneDrawAnother"), action: draw },
          later(t),
        ]
      : [
          {
            id: "draw",
            label: line(t, "askZoneDraw"),
            tone: "primary",
            action: draw,
          },
          {
            id: "none",
            label: line(t, "askZoneNone"),
            action: { kind: "accept" },
          },
          later(t),
        ],
  };
};

// No "Later": the circuits stay locked until there is a main board, so deferring it would strand him.
const mainAsk = (t: GapTranslate, main: MainBoardView): KarlAsk => {
  const known = main.status !== "none" && main.name && main.label;
  return {
    id: MAIN_BOARD_ASK_ID,
    group: "sources",
    question: known
      ? line(t, "askMainWhich", { board: main.name ?? "" })
      : line(t, "askMainUnknown"),
    detail: known && main.from ? line(t, `mainBoardFrom_${main.from}`) : undefined,
    subject: main.name ?? undefined,
    subjectKey: "main:floor",
    ...(main.position ? { focus: { position: main.position } } : {}),
    answers: known
      ? [
          {
            id: "yes",
            label: line(t, "askMainYes", { board: main.name ?? "" }),
            tone: "primary",
            action: { kind: "setMainBoard", label: main.label ?? "" },
          },
          {
            id: "other",
            label: line(t, "askMainShowOther"),
            action: { kind: "pickMainBoard", placesAs: "board" },
          },
          {
            id: "feedIn",
            label: line(t, "askMainFeedIn"),
            action: { kind: "pickMainBoard", placesAs: "feedIn" },
          },
        ]
      : [
          {
            id: "show",
            label: line(t, "askMainShow"),
            tone: "primary",
            action: { kind: "pickMainBoard", placesAs: "board" },
          },
          {
            id: "feedIn",
            label: line(t, "askMainFeedIn"),
            action: { kind: "pickMainBoard", placesAs: "feedIn" },
          },
        ],
  };
};

interface WorkingSet {
  names: string[];
  removed: number;
  added: number;
}

// Said as it is: the boards he works with are his set, not everything Karl found.
const completeQuestion = (t: GapTranslate, working: WorkingSet): string => {
  const names = working.names.join(", ");
  if (working.removed > 0) {
    return line(t, "askBoardsCompleteWorking", {
      count: working.names.length,
      names,
      removed: working.removed,
    });
  }
  if (working.added > 0) {
    return line(t, "askBoardsCompleteYours", { count: working.names.length, names });
  }
  return line(t, "askBoardsComplete", { count: working.names.length });
};

const completeAsk = (t: GapTranslate, working: WorkingSet): KarlAsk => ({
  id: BOARDS_COMPLETE_ASK_ID,
  group: "sources",
  question: completeQuestion(t, working),
  subjectKey: "boards:complete",
  tools: [
    {
      id: "addBoard",
      labelKey: "askToolAddBoard",
      action: { kind: "addSource" },
    },
  ],
  answers: [
    {
      id: "all",
      label: line(t, "askBoardsAllHere"),
      tone: "primary",
      action: { kind: "accept" },
    },
    later(t),
  ],
});

export const buildBoardAsks: BuildBoardAsks = ({
  roster,
  answeredKeys,
  names,
  main,
  feeds,
  heights,
  feeders,
  confirmedKeys = new Set<string>(),
  ...rest
}) => {
  const mainLabel =
    roster.find((entry) => entry.isMain && entry.label)?.label ?? null;
  const live = roster.filter(
    (entry) => !entry.isStruck || answeredKeys.has(entry.key),
  );
  const nameOf = (entry: (typeof roster)[number]) =>
    (entry.label && names[entry.label]) || entry.board;
  // A board he struck leaves the step, unless he answered about it in this walk and may step back.
  const asks = live.flatMap((entry) => {
    const name = nameOf(entry);
    const args = { ...rest, entry, name, mainLabel };
    const feeder = entry.label ? feeders?.byLabel[entry.label] : undefined;
    const label = entry.label || entry.key;
    // What the plan already holds of his answers about this board, ask by ask.
    const stored: Record<string, boolean> = {
      check: entry.isMoved || entry.status === "added",
      fedFrom: Boolean(feeder && (feeder.isStored || feeder.isOffFloor)),
      feederType: Boolean(feeder?.cableType),
      feeds: feeds?.byLabel[label]?.from === "yours",
      height: Boolean(entry.label && heights?.stored.boardHeights?.[entry.label] !== undefined),
    };
    // A board he said something about is one he counts.
    stored.consider = stored.fedFrom || stored.feederType || stored.feeds || stored.height;
    const tagged = (ask: KarlAsk | null, kind: string): KarlAsk | null =>
      ask && stored[kind] ? { ...ask, isStored: true } : ask;
    return [
      tagged(considerAsk(args), "consider"),
      tagged(checkAsk(args), "check"),
      tagged(feeders ? fedFromAsk(args, feeders, confirmedKeys) : null, "fedFrom"),
      tagged(feeders ? feederTypeAsk(args, feeders) : null, "feederType"),
      tagged(feeds ? feedsAsk(args, feeds) : null, "feeds"),
      tagged(heights ? heightAsk(args, heights) : null, "height"),
      zoneAsk(args),
    ].filter(
      (ask): ask is KarlAsk => ask !== null,
    );
  });
  const working: WorkingSet = {
    names: roster
      .filter((entry) => !entry.isStruck && entry.status !== "absent")
      .map(nameOf),
    removed: roster.filter((entry) => entry.isStruck).length,
    added: roster.filter((entry) => entry.status === "added").length,
  };
  const first =
    main && main.status !== "note"
      ? [{ ...mainAsk(rest.t, main), ...(main.status === "answered" ? { isStored: true } : {}) }]
      : [];
  const unfed = CATEGORY_ORDER.flatMap((category) => {
    const devices = feeds?.unfed[category] ?? 0;
    return devices > 0 ? [unfedAsk(rest.t, category, devices)] : [];
  });
  return [...first, ...asks, ...unfed, completeAsk(rest.t, working)];
};
